// Reading failures. The API answers problem+json everywhere except better-auth's `/v1/auth/*`,
// which answers `{ code, message }` (spec/08 §2.6); the generated client raises the two as
// `ApiProblemError` and `ApiAuthError`, and anything else as a transport failure.

import { ApiAuthError, ApiProblemError, type ProblemFieldError } from '@clavian/api-client';
import type { ErrorCode } from '@clavian/contracts';

/**
 * The problem codes the console branches on: the contract's error catalogue, snake_case. Each is checked against the
 * catalogue's own list (`ErrorCode`, derived from the slugs the server answers with), so a code the server never sends
 * — a misspelling, or the kebab-case slug `wallet-disabled` in place of the code `wallet_disabled` — fails to compile
 * rather than silently never matching (2026-10-12).
 */
export const PROBLEM = {
  forbidden: 'forbidden',
  notFound: 'not_found',
  secondFactorRequired: 'second_factor_required',
  settingLocked: 'setting_locked',
  entitlementRequired: 'entitlement_required',
  staleWrite: 'stale_write',
  validationFailed: 'validation_failed',
  slugTaken: 'slug_taken',
  conflict: 'conflict',
  // Moving a café from another system (F-HQ-40): an export that cannot be read, large balances not ticked, the GST
  // question unanswered, or money to carry into an organisation whose wallet is off.
  migrationFileUnreadable: 'migration_file_unreadable',
  migrationTicksMissing: 'migration_ticks_missing',
  migrationGstUnanswered: 'migration_gst_unanswered',
  walletDisabled: 'wallet_disabled',
  // A player's wallet moved at the counter (spec/06 §5): a top-up outside the limits or while top-ups are taxed at
  // sale, more cash than the wallet holds, an adjustment over the role's cap, refunds turned off.
  walletLimit: 'wallet_limit',
  walletTopupTaxAtSale: 'wallet_topup_tax_at_sale',
  walletInsufficientCash: 'wallet_insufficient_cash',
  walletAdjustmentOverLimit: 'wallet_adjustment_over_limit',
  walletRefundsOff: 'wallet_refunds_off',
  onlinePaymentsUnavailable: 'online_payments_unavailable',
  // Cash taken by someone holding no open drawer (ADR-0031): start a shift, or take card or UPI.
  cashNeedsShift: 'cash_needs_shift',
  alreadyAMember: 'already_a_member',
  unauthenticated: 'unauthenticated',
  // A phone number with no account yet: the terms have to be accepted first.
  consentRequired: 'consent_required',
  // `POST /v1/invitations/accept` (spec/07 §6.5).
  invitationUnavailable: 'invitation_unavailable',
  invitationContactMismatch: 'invitation_contact_mismatch',
  sharedDevice: 'shared_device',
  // Setting your own staff PIN (F-IAM-10): five wrong tries locked it until a manager resets it.
  staffPinLocked: 'staff_pin_locked',
  // The game catalogue (spec/14 §6, F-CAT-01): the metadata provider (IGDB) is not configured or
  // cannot answer (`503`), and a pick that resolves to no base game with a cover (`422`).
  catalogueProviderUnavailable: 'catalogue_provider_unavailable',
  notABaseGame: 'not_a_base_game',
  artSourceUnavailable: 'art_source_unavailable',
  // A request the server understood but will not carry out as sent (an audit export over its row cap, a release that names no registered build).
  unprocessable: 'unprocessable',
  otpDeliveryUnavailable: 'otp_delivery_unavailable',
  // The Floor drawer's Wake action: no online relay at the venue, or no address on record (F-STN-17).
  wakeUnavailable: 'wake_unavailable',
  // The sales pipeline (F-HQ-39): another lead already has the phone number.
  leadPhoneTaken: 'lead_phone_taken',
  leadConverted: 'lead_converted',
  // Starting a session from the console (F-SES-04, spec/05 §3.2): nothing funds it (`402`), the player is
  // under the class's age limit (`403`), the station cannot take one or the player is already playing (`409`).
  noFunding: 'no_funding',
  ageRestricted: 'age_restricted',
  stationUnavailable: 'station_unavailable',
  alreadyInSession: 'already_in_session',
  // A sale whose total moved since the cart was quoted (F-SES-17, spec/08 §2.9): the problem carries the fresh `total`.
  priceChanged: 'price_changed',
  sessionAlreadyEnded: 'session_already_ended',
  // The daily summary goes only to a verified address (F-ANL-07): "Email it to me" before verifying one.
  emailNotVerified: 'email_not_verified',
  // A venue's GST registration waits for the organisation's legal name and registered address (ADR-0046).
  businessDetailsRequired: 'business_details_required',
  // Phone sign-in turned off on the Platform (`auth.phoneSignIn`, 2026-10-08): an invitation to a phone number.
  phoneInvitationsOff: 'phone_invitations_off',
  // Going live before the essentials are set up: the problem names the steps still missing.
  venueNotReady: 'venue_not_ready',
  // Posting a stock count with items still uncounted: the problem's detail names how many.
  stockCountIncomplete: 'stock_count_incomplete',
} as const satisfies Readonly<Record<string, ErrorCode>>;

export function problemCode(error: unknown): string | undefined {
  if (error instanceof ApiProblemError || error instanceof ApiAuthError) return error.code;
  return undefined;
}

export function problemStatus(error: unknown): number | undefined {
  if (error instanceof ApiProblemError || error instanceof ApiAuthError) return error.status;
  return undefined;
}

/** A `412`: someone saved after this editor read (spec/08 §2.5). */
export const isStaleWrite = (error: unknown): boolean => problemStatus(error) === 412;

/** The console gate refused a session with a first factor only (spec/07 §6.6). */
export const needsSecondFactor = (error: unknown): boolean =>
  problemCode(error) === PROBLEM.secondFactorRequired || problemCode(error) === 'SECOND_FACTOR_REQUIRED';

/** The server's own words for a refusal (its `detail`), when it gave any — e.g. why a pass cannot be sold here. */
export function problemDetail(error: unknown): string | undefined {
  return error instanceof ApiProblemError ? error.problem.detail : undefined;
}

/** The fresh total a `price-changed` problem carries (F-SES-17), when it carries one. */
export function problemTotal(error: unknown): { readonly amount: number; readonly currency: string } | undefined {
  return error instanceof ApiProblemError ? error.problem.total : undefined;
}

export function fieldErrors(error: unknown): readonly ProblemFieldError[] {
  return error instanceof ApiProblemError ? error.fieldErrors : [];
}

export function requestIdOf(error: unknown): string | undefined {
  if (error instanceof ApiProblemError) return error.requestId;
  if (error instanceof ApiAuthError) return error.requestId;
  return undefined;
}
